<template>
  <div>
    <div class="flex items-center slide-title-edit">
      <div
        ref="titleContainer"
        class="presentation-title-container"
        :class="{'editable': isPresentationNameEditable}"
        @click.stop="openEditPresentationName"
        v-if="!openEditName && !isGuestUser"
        v-tooltip="{content: isPresentationNameEditable ? $i18nPresenter.t('Change presentation name') : null}"
      >
        <p
          class="mr-1 text-lg font-bold truncate sm:text-xl text-secondary-100 presentation-title"
          :class="{'default-presentation-name-color': isDefaultPresentationName}"
          :title="!isPresentationNameEditable ? presentation.name : null"
        >
          <span v-if="isBackstage" class="font-normal">{{ $i18nPresenter.t('Backstage of') }}</span>
          {{ presentation.name }}
        </p>
      </div>
    </div>
    <div
      class="edit-title-container"
      ref="slideTitleEditInputContainer"
      v-if="openEditName && !isGuestUser"
      v-click-outside="changePresentationName"
    >
      <aha-input
        class="text-lg font-bold sm:text-xl"
        type="text"
        ref="slideTitleEditInput"
        v-model="editPresentationName"
        :max-length="maxCharacters"
        @keyup.enter="updatePresentation({name: autoSetDefaultNameWhenEmpty()}, 'Enter')"
        @input="onChangeName"
        @keyup="onInput"
        autofocus
        autoresize
        :autoresizeWhen="'keydown'"
        :autoresizeOffset="60"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import debounce from 'debounce'
import utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import PresentationApi from '@/api/presentation'
import EventBus from '@/eventBus'
import Constant from '@/constant'
import Socket from '@/socket'

export default {
  props: {
    isSwitchingPresentationMode: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    openEditName: false,
    editPresentationName: '',
    maxCharacters: 100,
    debouncedChangeName: null,
  }),
  computed: {
    ...mapGetters(['isBackstage']),
    ...mapState({
      isPresenterWhiteLabel: (state) => state.isPresenterWhiteLabel,
      isGuestUser: (state) => state.currentUser.isGuest,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      canEditPresentation: (state) => state.canEditPresentation,
      presentation: (state) => state.presentation,
    }),
    isDefaultPresentationName() {
      return this.presentation.name === Constant.presentation.defaultPresentationName
    },
    isPresentationNameEditable() {
      return !this.isBackstage && this.canEditPresentation
    },
  },
  mounted() {
    EventBus.$on('open-edit-name', () => {
      this.openEditName = true
    })
  },
  destroyed() {
    EventBus.$off('open-edit-name')
  },
  methods: {
    ...mapActions(['setSyncLoadingAction', 'patchPresentationAction', 'setPresentation']),
    openEditPresentationName() {
      if (!this.isPresentationNameEditable) return
      this.openEditName = true
      this.editPresentationName = this.$store.state.presentation.name
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Editor Header > Presentation Name > Toggle Edit',
      )

      setTimeout(() => {
        this.$refs.slideTitleEditInput.$refs.input.focus()
        if (this.isDefaultPresentationName) {
          this.$refs.slideTitleEditInput.$refs.input.select()
        }
      }, 1)
    },
    async changePresentationName() {
      if (this.isSwitchingPresentationMode) return
      this.autoSetDefaultNameWhenEmpty()

      const payload = {
        name: this.editPresentationName,
      }

      if (!this.editPresentationName) {
        this._closePresentationEditName()
        return
      }
      if (this._isUnqualifiedPresentationName()) {
        this._closePresentationEditName()
        return
      }
      await this.updatePresentation(payload)
      this._closePresentationEditName()
      this.setPresentation(this.presentation)
    },

    autoSetDefaultNameWhenEmpty() {
      if (!this.editPresentationName.trim()) {
        this.editPresentationName = Constant.presentation.defaultPresentationName
      }
      return this.editPresentationName
    },

    _closePresentationEditName() {
      this.openEditName = false
    },

    _isUnqualifiedPresentationName() {
      if (!this.editPresentationName.replace(/\s/g, '') || this.editPresentationName === this.$store.state.presentation.name) {
        return true
      }
      return false
    },

    async updatePresentation(payload, e) {
      if (e && e === 'Enter') {
        // clear any queing update to presentation name, avoid double submit
        if (this.debouncedChangeName) this.debouncedChangeName.clear()
        this._closePresentationEditName()
        if (!this.editPresentationName || this._isUnqualifiedPresentationName()) return
      }
      this.setSyncLoadingAction(1)
      payload.socketId = Socket.getSocketId()
      let res

      try {
        res =
          await PresentationApi.updatePresentationByPresentationId(this.$route.params.id, payload)
      } catch (error) {
        this.setSyncLoadingAction(3)
        throw error
      }

      if (payload.name) {
        const [, changedRow] = res.body
        const { name, slugifiedName } = changedRow[0]
        this.patchPresentationAction({
          name,
          slugifiedName,
        })
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Editor Header > Presentation Name > Submit',
        )
      }
      this.setSyncLoadingAction(2)
    },

    onChangeName() {
      if (!this.debouncedChangeName) {
        // eslint-disable-next-line func-names
        this.debouncedChangeName = debounce(function () {
          if (!this.editPresentationName.replace(/\s/g, '')) return
          if (this.editPresentationName.length <= 100) {
            const payload = {
              name: this.editPresentationName,
            }
            this.updatePresentation(payload)
          }
        }, 1000)
      }
      this.debouncedChangeName()
    },

    onInput() {
      if (this.editPresentationName.length > 100) {
        this.editPresentationName = this.editPresentationName.substr(0, 100)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.presentation-title-container {
  border: 1px solid #ffffff00;
  border-radius: 0.25rem;
  padding: 0.3125rem 0.5rem;

  &:hover {
    cursor: default;

    &.editable {
      border-color: #ddd;
      cursor: text;
      border-radius: 5px ;
    }
  }

  .presentation-title {
    margin-right: 0;
  }

  .default-presentation-name-color {
    opacity: 0.5;
  }
}

.edit-title-container {
  //min-width: 250px;
  margin-right: 0.5rem;
  .aha-input__wrapper{
    min-width: 6rem;
  }
}

.presentation-title-container, .edit-title-container {
  @apply flex items-center group;
    @apply max-w-36;

  @media (min-width: 380px) {
    @apply max-w-48;
  }

  @media (min-width: 440px) {
    @apply max-w-60;
  }

  @media (min-width: 520px) {
    @apply max-w-80;
  }

  @media (min-width: 675px) {
    @apply max-w-76;
  }

  @media (min-width: 768px) and (max-width: 825px) {
    @apply max-w-48;
  }

  @media (min-width: 826px) and (max-width: 890px) {
    @apply max-w-60;
  }

  @media (min-width: 950px) and (max-width: 1024px) {
    @apply max-w-76;
  }

  @media (min-width: 1025px) and (max-width: 1064px) {
    @apply max-w-76;
  }

  @media (min-width: 1065px) {
    @apply max-w-76;
  }

  @media (min-width: 1368px) {
    @apply max-w-108;
  }

  @media (min-width: 1500px) {
    @apply max-w-140;
  }

  @media (min-width: 1800px) {
    max-width: none;
  }
}
</style>
